import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { DataCheckChip } from '../components/ds';
import { formatCurrency as safeFormatCurrency } from '../utils/currency';
import { todayIST, daysAgoIST, monthStartIST } from '../utils/istDate';
import { 
  TrendingUp, 
  TrendingDown,
  Users, 
  DollarSign, 
  Target,
  BarChart3,
  Download,
  Filter,
  Plus,
  ChevronDown,
  ChevronRight,
  Calendar,
  Globe,
  Megaphone,
  ArrowRight,
  Sparkles,
  RefreshCw,
  Upload,
  FileSpreadsheet,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import MultiSelectDropdown from '../components/MultiSelectDropdown';
import ViewLeadsModal from '../components/ViewLeadsModal';
import MetricTooltip from '../components/MetricTooltip';
import { getApiUrl } from '../utils/api';
import { gatedDownload } from '../lib/gatedDownload';

const API_URL = getApiUrl();

const MarketingDashboard = ({ currentUser }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [campaignPerformance, setCampaignPerformance] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [topCreatives, setTopCreatives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [syncing, setSyncing] = useState(false);
  const [refreshingMeta, setRefreshingMeta] = useState(false);
  
  // View Leads Modal
  const [viewLeadsModal, setViewLeadsModal] = useState(null); // { title, filters }
  
  const openLeadsModal = (title, leadFilters = {}) => {
    setViewLeadsModal({
      title,
      filters: {
        start_date: filters.start_date,
        end_date: filters.end_date,
        ...leadFilters,
      },
    });
  };
  
  // Filters
  // V35 — Staged filters: UI binds to `stagedFilters`. Network fetches react to
  // `appliedFilters`. Apply button commits staged → applied. Prevents the
  // multi-fetch storm that occurred when each filter input change auto-fired.
  const [datePreset, setDatePreset] = useState('last_7_days');
  const initialFilters = {
    start_date: daysAgoIST(7),
    end_date: todayIST(),
    country: [],
    campaign: '',
    source: [],
    ad_account_id: []
  };
  const [stagedFilters, setStagedFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  // Back-compat alias so existing usages of `filters` still resolve to applied.
  const filters = appliedFilters;
  const setFilters = setAppliedFilters;
  const filtersDirty = JSON.stringify(stagedFilters) !== JSON.stringify(appliedFilters);
  const applyStagedFilters = () => setAppliedFilters(stagedFilters);

  const applyDatePreset = (preset) => {
    setDatePreset(preset);
    if (preset === 'today') {
      const v = { start_date: todayIST(), end_date: todayIST() };
      setStagedFilters(f => ({ ...f, ...v }));
      setAppliedFilters(f => ({ ...f, ...v }));
    } else if (preset === 'yesterday') {
      const v = { start_date: daysAgoIST(1), end_date: daysAgoIST(1) };
      setStagedFilters(f => ({ ...f, ...v }));
      setAppliedFilters(f => ({ ...f, ...v }));
    } else if (preset === 'last_7_days') {
      const v = { start_date: daysAgoIST(7), end_date: todayIST() };
      setStagedFilters(f => ({ ...f, ...v }));
      setAppliedFilters(f => ({ ...f, ...v }));
    } else if (preset === 'mtd') {
      const v = { start_date: monthStartIST(), end_date: todayIST() };
      setStagedFilters(f => ({ ...f, ...v }));
      setAppliedFilters(f => ({ ...f, ...v }));
    }
    // 'custom' — don't change dates, user picks manually + clicks Apply
  };
  
  // Ad Accounts
  const [adAccounts, setAdAccounts] = useState([]);

  // V79 — per-section Campaign + Website (landing page) selectors
  const emptySectionFilter = { adAccounts: [], campaigns: [], websites: [] };
  const [sectionFilters, setSectionFilters] = useState({
    domestic: emptySectionFilter,
    international: emptySectionFilter,
    doctor: emptySectionFilter,
  });
  const [sectionOptions, setSectionOptions] = useState(null);
  const [sectionLoading, setSectionLoading] = useState(false);
  const [loadingSection, setLoadingSection] = useState(null);
  const pendingSection = useRef(null);
  const sectionReqId = useRef(0);
  const sectionFiltersMounted = useRef(false);

  const updateSectionFilter = (section, next) => {
    pendingSection.current = section;
    setSectionFilters((prev) => ({ ...prev, [section]: { ...emptySectionFilter, ...next } }));
  };
  
  // Campaign Modal
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [showSpendModal, setShowSpendModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);

  useEffect(() => {
    fetchAdAccounts();
  }, []);

  useEffect(() => {
    fetchAllData();
  }, [appliedFilters]);

  // V79 — a section-filter change only affects /dashboard, so refetch just that
  // (the campaign/creative tabs don't take section filters) and show a loading
  // state so stale numbers are never mistaken for filtered ones.
  useEffect(() => {
    if (!sectionFiltersMounted.current) {
      sectionFiltersMounted.current = true;
      return;
    }
    const myReq = ++sectionReqId.current;
    const whichSection = pendingSection.current;
    const fetchDashboardOnly = async () => {
      setSectionLoading(true);
      setLoadingSection(whichSection);
      try {
        const res = await axios.get(`${API_URL}/api/marketing/dashboard?${buildDashboardParams()}`, { withCredentials: true });
        if (myReq !== sectionReqId.current) return;  // stale response guard
        setDashboardData(res.data);
      } catch (error) {
        if (myReq === sectionReqId.current) toast.error('Failed to apply section filters');
      } finally {
        if (myReq === sectionReqId.current) {
          setSectionLoading(false);
          setLoadingSection(null);
        }
      }
    };
    fetchDashboardOnly();
  }, [sectionFilters]);

  // V79 — Campaign options are date-scoped (Meta insights for the period)
  useEffect(() => {
    const fetchSectionOptions = async () => {
      try {
        const p = new URLSearchParams();
        if (filters.start_date) p.append('start_date', filters.start_date);
        if (filters.end_date) p.append('end_date', filters.end_date);
        const res = await axios.get(`${API_URL}/api/marketing/section-filter-options?${p}`, { withCredentials: true });
        setSectionOptions(res.data);
      } catch (error) {
        console.error('Error fetching section filter options:', error);
      }
    };
    fetchSectionOptions();
  }, [appliedFilters.start_date, appliedFilters.end_date]);

  const fetchAdAccounts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/marketing/ad-accounts`, { withCredentials: true });
      setAdAccounts(response.data);
    } catch (error) {
      console.error('Error fetching ad accounts:', error);
    }
  };

  const buildDashboardParams = () => {
    const params = new URLSearchParams();
    if (filters.start_date) params.append('start_date', filters.start_date);
    if (filters.end_date) params.append('end_date', filters.end_date);
    if (filters.country.length) params.append('country', filters.country.join(','));
    if (filters.campaign) params.append('campaign', filters.campaign);
    if (filters.source.length) params.append('source', filters.source.join(','));
    if (filters.ad_account_id.length) params.append('ad_account_id', filters.ad_account_id.join(','));
    // V79 — per-section cascading filter: Ad Account → Campaign → Landing Page
    ['domestic', 'international', 'doctor'].forEach((sec) => {
      const sf = sectionFilters[sec] || {};
      if (sf.adAccounts?.length) params.append(`${sec}_ad_account`, sf.adAccounts.join(','));
      // campaign names contain commas → joined with '||'
      if (sf.campaigns?.length) params.append(`${sec}_campaign`, sf.campaigns.join('||'));
      if (sf.websites?.length) params.append(`${sec}_website`, sf.websites.join(','));
    });
    return params;
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const params = buildDashboardParams();

      const [dashRes, perfRes, campRes, creativeRes] = await Promise.allSettled([
        axios.get(`${API_URL}/api/marketing/dashboard?${params}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/marketing/campaign-performance?${params}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/marketing/campaigns`, { withCredentials: true }),
        axios.get(`${API_URL}/api/marketing/top-creatives?${params}`, { withCredentials: true })
      ]);

      if (dashRes.status === 'fulfilled') setDashboardData(dashRes.value.data);
      else console.error('Dashboard API failed:', dashRes.reason?.response?.status, dashRes.reason?.response?.data);

      if (perfRes.status === 'fulfilled') setCampaignPerformance(perfRes.value.data.campaigns || []);
      else console.error('Campaign performance API failed:', perfRes.reason?.response?.status, perfRes.reason?.response?.data);

      if (campRes.status === 'fulfilled') setCampaigns(campRes.value.data || []);
      else console.error('Campaigns API failed:', campRes.reason?.response?.status, campRes.reason?.response?.data);

      if (creativeRes.status === 'fulfilled') setTopCreatives(creativeRes.value.data || []);
      else console.error('Top creatives API failed:', creativeRes.reason?.response?.status, creativeRes.reason?.response?.data);

      const allFailed = [dashRes, perfRes, campRes, creativeRes].every(r => r.status === 'rejected');
      if (allFailed) toast.error('Failed to load marketing data. Please try re-logging in.');
    } catch (error) {
      console.error('Error fetching marketing data:', error);
      toast.error('Failed to load marketing data');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      
      const saved = await gatedDownload(
        `/api/marketing/export/campaigns?${params}`,
        `campaign_performance_${filters.start_date}_to_${filters.end_date}.csv`
      );
      if (saved) toast.success('Export downloaded');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const handleRefreshFromMeta = async () => {
    setRefreshingMeta(true);
    try {
      // Fetch fresh data from Meta Ads API
      const params = new URLSearchParams();
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.ad_account_id.length) params.append('ad_account_id', filters.ad_account_id.join(','));
      
      await axios.post(`${API_URL}/api/marketing-intel/refresh`, {}, { withCredentials: true });
      toast.success('Data refreshed from Meta');
      fetchAllData();
      fetchAdAccounts();
    } catch (error) {
      toast.error('Failed to refresh from Meta');
    } finally {
      setRefreshingMeta(false);
    }
  };

  const formatCurrency = (amount) => safeFormatCurrency(amount, 'INR', { maximumFractionDigits: 0 });

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  if (loading && !dashboardData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const kpis = dashboardData?.kpis || {};
  const funnel = dashboardData?.funnel || { stages: [] };
  const qualityBreakdown = dashboardData?.quality_breakdown || {};

  return (
    <div data-testid="marketing-dashboard" className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-foreground">Marketing Dashboard</h1>
          <p className="text-muted-foreground">Track campaign performance and growth metrics</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <DataCheckChip startDate={filters.start_date} endDate={filters.end_date} />
          {/* Refresh from Meta */}
          <button
            onClick={handleRefreshFromMeta}
            disabled={refreshingMeta}
            className="btn-secondary flex items-center gap-2 text-sm"
            data-testid="refresh-meta-btn"
            title="Pull latest data from Meta Ads"
          >
            <RefreshCw className={`w-4 h-4 ${refreshingMeta ? 'animate-spin' : ''}`} />
            {refreshingMeta ? 'Refreshing...' : 'Refresh from Meta'}
          </button>
          
          <div className="w-px h-6 bg-slate-200" />
          
          <button
            onClick={() => setShowCampaignModal(true)}
            className="btn-secondary flex items-center gap-2 text-sm"
            data-testid="add-campaign-btn"
          >
            <Plus className="w-4 h-4" />
            Add Campaign
          </button>
          <button
            onClick={handleExport}
            className="btn-primary flex items-center gap-2 text-sm"
            data-testid="export-btn"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-slate-500" />
              <span className="text-sm font-medium text-slate-700">Filters</span>
              {filtersDirty && (
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold"
                      data-testid="filters-dirty-badge">
                  Unapplied changes
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setStagedFilters(appliedFilters)}
                disabled={!filtersDirty}
                className="px-3 py-1.5 text-xs rounded-md font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                data-testid="filters-reset-btn"
              >
                Reset
              </button>
              <button
                onClick={applyStagedFilters}
                disabled={!filtersDirty}
                className={`px-4 py-1.5 text-xs rounded-md font-medium transition-colors ${
                  filtersDirty
                    ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                    : 'bg-slate-100 text-slate-400 cursor-not-allowed'
                }`}
                data-testid="filters-apply-btn"
              >
                Apply Filters
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label className="block text-xs text-slate-500 mb-1">Date Range</label>
              <div className="flex gap-1.5 mb-2">
                {[
                  { key: 'today', label: 'Today' },
                  { key: 'yesterday', label: 'Yesterday' },
                  { key: 'last_7_days', label: 'Last 7 Days' },
                  { key: 'mtd', label: 'MTD' },
                  { key: 'custom', label: 'Custom' },
                ].map(p => (
                  <button
                    key={p.key}
                    onClick={() => applyDatePreset(p.key)}
                    className={`px-3 py-1.5 text-xs rounded-md font-medium transition-colors ${
                      datePreset === p.key
                        ? 'bg-indigo-600 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                    data-testid={`date-preset-${p.key}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              {datePreset === 'custom' && (
                <div className="flex gap-2">
                  <input
                    type="date"
                    value={stagedFilters.start_date}
                    onChange={(e) => setStagedFilters({ ...stagedFilters, start_date: e.target.value })}
                    className="input-base text-sm flex-1"
                    data-testid="custom-start-date"
                  />
                  <input
                    type="date"
                    value={stagedFilters.end_date}
                    onChange={(e) => setStagedFilters({ ...stagedFilters, end_date: e.target.value })}
                    className="input-base text-sm flex-1"
                    data-testid="custom-end-date"
                  />
                </div>
              )}
            </div>
            {/* V36 Phase 2 — Country / Source / Ad Account filters removed per user direction (Jun 2026).
                Section bucketing happens server-side via canonical helpers; client-side overrides
                only added confusion. */}
            <div>
              <label className="block text-xs text-slate-500 mb-1">Campaign</label>
              <input
                type="text"
                value={stagedFilters.campaign}
                onChange={(e) => setStagedFilters({ ...stagedFilters, campaign: e.target.value })}
                placeholder="Search campaign..."
                className="input-base text-sm"
              />
            </div>
          </div>
        </div>
      </div>

      {/* V35 — 3-section Marketing Dashboard: Domestic / International / Doctor Referrals.
          Each section has Page Views, Total Leads, Trial attribution (Marketing vs Pre-Sales),
          Sales Revenue per native currency (dynamic — USD/AUD/EUR cards auto-appear),
          Ad Spend (INR — we pay Meta in INR for every account), ROAS, CPL, CPT, CPE,
          AOV, Sales Conversion, Checkout Conversion, Avg Time to Convert. */}
      {dashboardData?.sections && (
        <div className="space-y-6" data-testid="v35-section-blocks">
          <SectionBlock
            label="Domestic · India Funnel"
            flag="🇮🇳"
            accent="indigo"
            data={dashboardData.sections.domestic}
            onViewLeads={openLeadsModal}
            options={sectionOptions}
            sectionFilter={sectionFilters.domestic}
            onSectionFilterChange={(next) => updateSectionFilter('domestic', next)}
            refreshing={sectionLoading && loadingSection === 'domestic'}
          />
          <SectionBlock
            label="International · Rest of World"
            flag="🌍"
            accent="cyan"
            data={dashboardData.sections.international}
            onViewLeads={openLeadsModal}
            options={sectionOptions}
            sectionFilter={sectionFilters.international}
            onSectionFilterChange={(next) => updateSectionFilter('international', next)}
            refreshing={sectionLoading && loadingSection === 'international'}
          />
          <SectionBlock
            label="Doctor Referrals"
            flag="🩺"
            accent="emerald"
            data={dashboardData.sections.doctor}
            onViewLeads={openLeadsModal}
            options={sectionOptions}
            sectionFilter={sectionFilters.doctor}
            onSectionFilterChange={(next) => updateSectionFilter('doctor', next)}
            refreshing={sectionLoading && loadingSection === 'doctor'}
            placeholder="Section reserved for doctor-referred leads. No data flowing yet — activates when the doctor referral program goes live."
          />
        </div>
      )}

      {/* V36 Phase 2 — Legacy top-level KPI grid (Total Leads / Enrolments / Revenue /
          Ad Spend / ROAS + CPL / CPT / CPA / AOV / Avg Time) removed per user direction
          (Jun 2026). The three SectionBlocks above are the canonical source of truth
          for every metric — same numbers, properly scoped by section. */}

      {/* Tabs */}
      <div className="border-b border-slate-200">
        <nav className="flex gap-6 overflow-x-auto">
          {/* V36 Phase 2 — Sales Metrics tab removed per user direction (Jun 2026).
              SectionBlocks above already cover all marketing-side sales metrics. */}
          {['overview', 'campaigns', 'creatives', 'lead-quality', 'funnel', 'ai-strategy'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab 
                  ? 'border-indigo-600 text-indigo-600' 
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              {{'lead-quality': 'Lead Quality', 'ai-strategy': 'AI Strategy', 'funnel': 'Funnel Analytics'}[tab] || tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {/* V36 Phase 2 — Sales Metrics tab block removed. */}

      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Funnel Visualization */}
          <div className="card">
            <div className="card-header">
              <h3 className="font-semibold text-slate-900">Conversion Funnel</h3>
            </div>
            <div className="card-body">
              <div className="space-y-4">
                {funnel.stages.map((stage, idx) => (
                  <div key={stage.name} className="relative">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-slate-700">{stage.name}</span>
                      <span className="text-sm text-slate-500">{stage.count}</span>
                    </div>
                    <div className="h-8 bg-slate-100 rounded-lg overflow-hidden">
                      <div 
                        className={`h-full transition-all duration-500 ${
                          idx === 0 ? 'bg-blue-500' :
                          idx === 1 ? 'bg-indigo-500' :
                          idx === 2 ? 'bg-purple-500' : 'bg-emerald-500'
                        }`}
                        style={{ width: `${stage.conversion}%` }}
                      />
                    </div>
                    {idx < funnel.stages.length - 1 && (
                      <div className="flex items-center justify-center my-2">
                        <ArrowRight className="w-4 h-4 text-slate-400" />
                        <span className="text-xs text-slate-500 ml-1">
                          {funnel.stages[idx + 1]?.conversion}% conversion
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-4 pt-4 border-t border-slate-200">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-700">Overall Conversion</span>
                  <span className="text-lg font-bold text-emerald-600">{funnel.overall_conversion}%</span>
                </div>
              </div>
            </div>
          </div>

          {/* Lead Quality */}
          <div className="card">
            <div className="card-header">
              <h3 className="font-semibold text-slate-900">Lead Quality Distribution</h3>
            </div>
            <div className="card-body">
              <div className="space-y-4">
                <QualityBar label="Good" count={qualityBreakdown.good || 0} total={kpis.total_leads} color="emerald" />
                <QualityBar label="Average" count={qualityBreakdown.average || 0} total={kpis.total_leads} color="amber" />
                <QualityBar label="Bad" count={qualityBreakdown.bad || 0} total={kpis.total_leads} color="red" />
                <QualityBar label="Not Rated" count={qualityBreakdown.not_rated || 0} total={kpis.total_leads} color="slate" />
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'campaigns' && (
        <div className="card">
          <div className="card-header flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-slate-900">Campaign Performance</h3>
              <p className="text-sm text-slate-500">Leads &amp; trials acquired in period · enrolments + ₹ revenue earned in period, traced back to campaign</p>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="p-3 text-left text-xs font-medium text-slate-500 min-w-[250px]">Campaign</th>
                  <th className="p-3 text-center text-xs font-medium text-slate-500" title="Good / Average / Bad">
                    Quality (G/A/B)
                  </th>
                  <th className="p-3 text-right text-xs font-medium text-slate-500" title="% of Good Leads">
                    Quality %
                  </th>
                  <th className="p-3 text-right text-xs font-medium text-slate-500" title="Paid trials + Unpaid form fills">Leads</th>
                  <th className="p-3 text-right text-xs font-medium text-slate-500">Paid Trials</th>
                  <th className="p-3 text-right text-xs font-medium text-slate-500">Spend</th>
                  <th className="p-3 text-right text-xs font-medium text-slate-500" title="Spend / Total Leads">CPL</th>
                  <th className="p-3 text-right text-xs font-medium text-slate-500" title="Spend / Paid Trials">Cost/Trial</th>
                  <th className="p-3 text-right text-xs font-medium text-slate-500" title="Enrolments in the selected period, traced back to this campaign">Enrolments</th>
                  <th className="p-3 text-right text-xs font-medium text-slate-500" title="Spend / Enrolments">Cost/Enrol</th>
                  <th className="p-3 text-right text-xs font-medium text-slate-500" title="Enrolment revenue in period, ₹ FX-adjusted">Revenue (₹)</th>
                  <th className="p-3 text-right text-xs font-medium text-slate-500">ROAS</th>
                </tr>
              </thead>
              <tbody>
                {campaignPerformance.length === 0 ? (
                  <tr>
                    <td colSpan="12" className="p-8 text-center text-slate-500">
                      No campaign data available. Add campaigns and spend to see performance.
                    </td>
                  </tr>
                ) : (
                  campaignPerformance.map((camp, idx) => (
                    <tr key={idx} className={`border-b border-slate-100 hover:bg-slate-50 ${camp.is_unmatched ? 'bg-amber-50/60 border-l-2 border-l-amber-400' : ''}`} data-testid={camp.is_unmatched ? 'campaign-row-unmatched' : `campaign-row-${idx}`}>
                      <td className="p-3">
                        <div className="font-medium text-slate-900 text-sm" title={camp.campaign_name}>
                          {camp.campaign_name}
                        </div>
                      </td>
                      <td className="p-3 text-center">
                        <div className="flex items-center justify-center gap-1 text-xs">
                          <span className="text-emerald-600 font-medium">{camp.good_leads || 0}</span>
                          <span className="text-slate-400">/</span>
                          <span className="text-amber-600 font-medium">{camp.avg_leads || 0}</span>
                          <span className="text-slate-400">/</span>
                          <span className="text-red-600 font-medium">{camp.bad_leads || 0}</span>
                        </div>
                      </td>
                      <td className="p-3 text-right">
                        <span className={`text-sm font-medium ${
                          (camp.quality_score || 0) >= 50 ? 'text-emerald-600' : 
                          (camp.quality_score || 0) >= 25 ? 'text-amber-600' : 'text-red-600'
                        }`}>
                          {camp.quality_score || 0}%
                        </span>
                      </td>
                      <td className="p-3 text-right text-sm font-medium">
                        {camp.is_unmatched ? (
                          <span className="text-slate-700">{camp.leads}</span>
                        ) : (
                          <button
                            onClick={() => openLeadsModal(
                              `Leads · ${camp.campaign_name}`,
                              { metric: 'campaign_leads', campaign_id: camp.campaign_id }
                            )}
                            className="text-indigo-600 hover:text-indigo-800 hover:underline font-medium"
                            data-testid={`camp-leads-${camp.campaign_id}`}
                            title="View attributed leads"
                          >
                            {camp.leads}
                          </button>
                        )}
                      </td>
                      <td className="p-3 text-right text-sm font-medium">
                        {camp.is_unmatched ? (
                          <span className="text-slate-700">{camp.paid_trials || 0}</span>
                        ) : (
                          <button
                            onClick={() => openLeadsModal(
                              `Paid Trials · ${camp.campaign_name}`,
                              { metric: 'campaign_paid_trials', campaign_id: camp.campaign_id }
                            )}
                            className="text-indigo-600 hover:text-indigo-800 hover:underline font-medium"
                            data-testid={`camp-trials-${camp.campaign_id}`}
                            title="View paid-trial leads"
                          >
                            {camp.paid_trials || 0}
                          </button>
                        )}
                      </td>
                      <td className="p-3 text-right text-sm">{formatCurrency(camp.spend)}</td>
                      <td className="p-3 text-right text-sm">{formatCurrency(camp.cpl)}</td>
                      <td className="p-3 text-right text-sm">{formatCurrency(camp.cost_per_trial || 0)}</td>
                      <td className="p-3 text-right text-sm font-medium">{camp.enrollments || 0}</td>
                      <td className="p-3 text-right text-sm">{camp.cost_per_enrolment ? formatCurrency(camp.cost_per_enrolment) : '—'}</td>
                      <td className="p-3 text-right text-sm font-medium text-emerald-600">
                        {formatCurrency(camp.revenue)}
                      </td>
                      <td className="p-3 text-right">
                        <span className={`text-sm font-medium ${camp.roas >= 1 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {camp.roas?.toFixed(2) || '0.00'}x
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'creatives' && (
        <CreativesTab 
          topCreatives={topCreatives}
          filters={filters}
          formatCurrency={formatCurrency}
          formatNumber={formatNumber}
          onRefresh={fetchAllData}
          currentUser={currentUser}
        />
      )}

      {activeTab === 'lead-quality' && (
        <LeadQualityTab
          filters={filters}
          formatCurrency={formatCurrency}
          formatNumber={formatNumber}
        />
      )}

      {activeTab === 'funnel' && (
        <FunnelTab filters={filters} />
      )}

      {activeTab === 'ai-strategy' && (
        <AIStrategyTab filters={filters} adAccounts={adAccounts} />
      )}

      {/* Campaign Modal */}
      {showCampaignModal && (
        <CampaignModal
          onClose={() => setShowCampaignModal(false)}
          onSave={() => {
            setShowCampaignModal(false);
            fetchAllData();
          }}
        />
      )}

      {/* Spend Modal */}
      {showSpendModal && selectedCampaign && (
        <SpendModal
          campaign={selectedCampaign}
          onClose={() => {
            setShowSpendModal(false);
            setSelectedCampaign(null);
          }}
          onSave={() => {
            setShowSpendModal(false);
            setSelectedCampaign(null);
            fetchAllData();
          }}
        />
      )}

      {/* View Leads Modal */}
      {viewLeadsModal && (
        <ViewLeadsModal
          title={viewLeadsModal.title}
          filters={viewLeadsModal.filters}
          onClose={() => setViewLeadsModal(null)}
          currentUser={currentUser}
        />
      )}
    </div>
  );
};

// KPI Card Component
const KPICard = ({ title, value, icon: Icon, color, onViewLeads }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    purple: 'bg-purple-50 text-purple-600',
    red: 'bg-red-50 text-red-600',
    amber: 'bg-amber-50 text-amber-600'
  };

  return (
    <div className="card">
      <div className="card-body">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs text-slate-500 mb-1">{title}</div>
            <div className="text-xl font-bold text-slate-900">{value}</div>
            {onViewLeads && (
              <button onClick={onViewLeads} className="text-xs text-indigo-600 hover:text-indigo-800 font-medium mt-1 inline-block transition-colors" data-testid={`kpi-${title.toLowerCase().replace(/\s+/g, '-')}-view-leads`}>
                View Leads &rarr;
              </button>
            )}
          </div>
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// V35 — 3-Section Marketing Dashboard components
// SectionBlock (Domestic / International / Doctor Referrals) +
// MBlock (single metric tile, dynamic currency cards inside International).
// ════════════════════════════════════════════════════════════════════════════

const CURRENCY_SYMBOL = {
  INR: '₹', USD: '$', AUD: 'A$', EUR: '€', GBP: '£', CAD: 'C$', SGD: 'S$', AED: 'AED ', NZD: 'NZ$',
};
const CURRENCY_LOCALE = {
  INR: 'en-IN', USD: 'en-US', AUD: 'en-AU', EUR: 'en-GB',
  GBP: 'en-GB', CAD: 'en-CA', SGD: 'en-SG', AED: 'en-AE', NZD: 'en-NZ',
};

const formatCurrencyNative = (currency, amount) => {
  const sym = CURRENCY_SYMBOL[currency] || `${currency} `;
  const loc = CURRENCY_LOCALE[currency] || 'en-US';
  const decimals = currency === 'INR' ? 0 : 2;
  return `${sym}${(Number(amount) || 0).toLocaleString(loc, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`;
};

const SectionBlock = ({ label, flag, accent, data, onViewLeads, placeholder, options, sectionFilter, onSectionFilterChange, refreshing }) => {
  const accentBar = {
    indigo:  'from-indigo-500 to-indigo-700',
    cyan:    'from-cyan-500 to-sky-700',
    emerald: 'from-emerald-500 to-emerald-700',
  }[accent] || 'from-slate-500 to-slate-700';

  // V79 — applied per-section selection (Ad Account → Campaign → Landing Page)
  const selAccounts = sectionFilter?.adAccounts || [];
  const selCampaigns = sectionFilter?.campaigns || [];
  const selWebsites = sectionFilter?.websites || [];
  const [draft, setDraft] = useState({ adAccounts: selAccounts, campaigns: selCampaigns, websites: selWebsites });
  useEffect(() => {
    setDraft({
      adAccounts: sectionFilter?.adAccounts || [],
      campaigns: sectionFilter?.campaigns || [],
      websites: sectionFilter?.websites || [],
    });
  }, [sectionFilter]);

  if (!data) return null;

  const fmtNumIN = (n) => (Number(n) || 0).toLocaleString('en-IN');
  const fmtINR   = (n) => `₹${fmtNumIN(Math.round(Number(n) || 0))}`;

  const totalTrials = data.trial_paid_total || 0;
  const marketingShare = data.marketing_share_pct ?? 100;

  // V35.2 — Domestic = INR-only cards. International = INR + USD + dynamic single-card
  // per non-INR-non-USD currency. Doctor = placeholder until program activates.
  const trialByCurrency  = data.trial_revenue_by_currency  || {};
  const salesByCurrency  = data.sales_revenue_by_currency  || {};
  const renewalByCurrency = data.renewal_revenue_by_currency || {};
  const firstEnrolByCurrency = data.first_enrol_revenue_by_currency || {};
  const allRevByCurrency = data.all_revenue_by_currency    || {};
  const isDomestic       = data.section === 'domestic';
  const isInternational  = data.section === 'international';
  const isDoctor         = data.section === 'doctor';
  const extraCurrencies  = Object.keys(allRevByCurrency)
    .filter((c) => c !== 'INR' && c !== 'USD')
    .filter((c) => (allRevByCurrency[c]?.sum || 0) > 0)
    .sort();
  const extraRenewalCurrencies = Object.keys(renewalByCurrency)
    .filter((c) => c !== 'INR' && c !== 'USD')
    .filter((c) => (renewalByCurrency[c]?.sum || 0) > 0)
    .sort();

  const isEmpty = (data.total_leads || 0) === 0 && totalTrials === 0;

  // V36 — Filter-basis tooltip suffix per metric
  const FILTER_BY_CREATED = '⏱ Filtered by lead created date.';
  const FILTER_BY_CAPTURED = '⏱ Filtered by payment capture date.';

  // V36 — Ad Spend tooltip lists the account breakdown
  const adAcctBreakdown = data.ad_account_breakdown || [];
  const adAcctTooltip = adAcctBreakdown.length > 0
    ? adAcctBreakdown.map(a => `• ${a.account_name} (${a.account_id}): ${fmtINR(a.spend_inr)}`).join('\n')
    : 'No ad accounts wired to this section.';

  // V79 — selector options for this section (campaigns scoped to its ad accounts)
  const sectionOpts = options?.sections?.[data.section] || {};
  const sectionAccounts = sectionOpts.ad_accounts || adAcctBreakdown.map(a => ({
    account_id: a.account_id, account_name: a.account_name,
  }));
  const campaignOptions = sectionOpts.campaigns || [];
  const websiteOptions = (options?.websites || []).map(w => ({ value: w.key, label: w.label }));

  // V36 — International "Sales Revenue (INR)" shows the INR-equivalent rollup with FX breakdown
  const fxBreakdown = data.sales_revenue_fx_breakdown || [];
  const fxBreakdownText = fxBreakdown.length > 0
    ? fxBreakdown
        .filter(b => b.native_amount > 0)
        .map(b => `${b.currency} ${b.native_amount.toLocaleString()} × ${b.fx_rate} = ${fmtINR(b.inr_equivalent)}`)
        .join('  +  ')
    : '';

  // V35.2 + V36 — every onClick carries the section so the View Leads modal stays scoped.
  // V79 — also carries the section's applied Ad Account → Campaign → Landing Page
  // selection so the modal list matches the card number exactly.
  // An Ad Account selection reaches leads through its campaign names (a lead
  // carries a campaign token, never an account id) — same translation the
  // backend does for the cards.
  const modalCampaignKeys = selCampaigns.length
    ? selCampaigns
    : (selAccounts.length
        ? campaignOptions
            .filter(c => (c.account_ids || []).some(a => selAccounts.includes(a)))
            .map(c => c.value)
        : []);
  const openModal = (title, extraFilters = {}) => () =>
    onViewLeads(`${title} · ${data.section}`, {
      ...extraFilters,
      section: data.section,
      ...(modalCampaignKeys.length ? { campaign_keys: modalCampaignKeys.join('||') } : {}),
      ...(selWebsites.length ? { website: selWebsites.join(',') } : {}),
    });

  // V79 — staged (draft) selection; nothing refetches until Apply is pressed.
  // Hooks must run before the `if (!data) return null` early-return below.
  const accountOptions = sectionAccounts.map(a => ({ value: a.account_id, label: a.account_name }));
  // Cascade: campaign list narrows to the chosen ad account(s)
  const visibleCampaignOptions = draft.adAccounts.length
    ? campaignOptions.filter(c => (c.account_ids || []).some(a => draft.adAccounts.includes(a)))
    : campaignOptions;

  const onAccountsChange = (v) => setDraft(d => ({
    ...d,
    adAccounts: v,
    // drop campaigns that no longer belong to the selected account(s)
    campaigns: v.length
      ? d.campaigns.filter(name => {
          const o = campaignOptions.find(c => c.value === name);
          return o && (o.account_ids || []).some(a => v.includes(a));
        })
      : d.campaigns,
  }));

  const isDirty = JSON.stringify([draft.adAccounts, draft.campaigns, draft.websites])
    !== JSON.stringify([selAccounts, selCampaigns, selWebsites]);
  const hasApplied = selAccounts.length > 0 || selCampaigns.length > 0 || selWebsites.length > 0;

  return (
    <section className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
             data-testid={`section-block-${data.section}`}>
      <header className={`bg-gradient-to-r ${accentBar} text-white px-5 py-3`}>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="min-w-0">
            <h3 className="font-semibold text-base flex items-center gap-2">
              <span className="text-lg">{flag}</span>{label}
            </h3>
            {/* V79 — which Meta ad account(s) feed this section */}
            {sectionAccounts.length > 0 && (
              <div className="flex items-center gap-1.5 flex-wrap mt-1.5"
                   data-testid={`section-ad-accounts-${data.section}`}>
                <span className="text-[10px] uppercase tracking-wider text-white/70">Ad Account{sectionAccounts.length > 1 ? 's' : ''}</span>
                {sectionAccounts.map((a) => (
                  <span key={a.account_id}
                        className="text-[11px] bg-white/15 px-2 py-0.5 rounded-full backdrop-blur-sm"
                        title={`Account ID: ${a.account_id}`}>
                    {a.account_name}
                  </span>
                ))}
              </div>
            )}
          </div>
          {totalTrials > 0 && (
            <span className="text-xs bg-white/15 px-3 py-1 rounded-full backdrop-blur-sm">
              <strong>{marketingShare}%</strong> Marketing · <strong>{(100 - marketingShare).toFixed(1)}%</strong> Pre-Sales
            </span>
          )}
        </div>
      </header>

      {/* V79 — Section-scoped cascading filter: Ad Account → Campaign → Landing
          Page. Staged selection; press Apply to recalculate this section only. */}
      {onSectionFilterChange && (
        <div className="px-4 md:px-5 py-3 bg-slate-50 border-b border-slate-200"
             data-testid={`section-filters-${data.section}`}>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-slate-500 mb-1 font-medium">1 · Ad Account</label>
              <MultiSelectDropdown
                options={accountOptions}
                selected={draft.adAccounts}
                onChange={onAccountsChange}
                placeholder="All Ad Accounts"
                testId={`section-adaccount-${data.section}`}
              />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-slate-500 mb-1 font-medium">2 · Campaign</label>
              <MultiSelectDropdown
                options={visibleCampaignOptions}
                selected={draft.campaigns}
                onChange={(v) => setDraft(d => ({ ...d, campaigns: v }))}
                placeholder={draft.adAccounts.length ? 'All Campaigns in account' : 'All Campaigns'}
                testId={`section-campaign-${data.section}`}
              />
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-slate-500 mb-1 font-medium">3 · Website / Landing Page</label>
              <MultiSelectDropdown
                options={websiteOptions}
                selected={draft.websites}
                onChange={(v) => setDraft(d => ({ ...d, websites: v }))}
                placeholder="All Websites"
                testId={`section-website-${data.section}`}
              />
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => onSectionFilterChange(draft)}
                disabled={!isDirty || refreshing}
                className={`text-xs px-4 py-2 rounded-md font-semibold transition-colors ${
                  isDirty && !refreshing
                    ? 'bg-slate-900 text-white hover:bg-slate-700'
                    : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                }`}
                data-testid={`section-filters-apply-${data.section}`}
              >
                Apply
              </button>
              {(hasApplied || isDirty) && (
                <button
                  onClick={() => {
                    setDraft({ adAccounts: [], campaigns: [], websites: [] });
                    if (hasApplied) onSectionFilterChange({ adAccounts: [], campaigns: [], websites: [] });
                  }}
                  className="text-xs px-3 py-2 rounded-md bg-slate-200 text-slate-700 hover:bg-slate-300 font-medium transition-colors"
                  data-testid={`section-filters-clear-${data.section}`}
                >
                  Clear
                </button>
              )}
            </div>
          </div>
          <div className="mt-2 min-h-[18px]">
            {refreshing ? (
              <span className="text-[11px] text-indigo-700 bg-indigo-50 px-2.5 py-1 rounded-md font-medium inline-flex items-center gap-1.5"
                    data-testid={`section-filters-loading-${data.section}`}>
                <RefreshCw className="w-3 h-3 animate-spin" />
                Updating numbers…
              </span>
            ) : isDirty ? (
              <span className="text-[11px] text-slate-500">
                Selection staged — press <strong>Apply</strong> to recalculate this section.
              </span>
            ) : hasApplied ? (
              <span className="text-[11px] text-amber-700 bg-amber-100 px-2 py-1 rounded-md font-medium"
                    data-testid={`section-filters-active-${data.section}`}>
                Filtered view
                {selAccounts.length ? ` · ${selAccounts.length} ad account${selAccounts.length > 1 ? 's' : ''}` : ''}
                {selCampaigns.length ? ` · ${selCampaigns.length} campaign${selCampaigns.length > 1 ? 's' : ''}` : ''}
                {selWebsites.length ? ` · ${selWebsites.length} website${selWebsites.length > 1 ? 's' : ''}` : ''}
              </span>
            ) : (
              <span className="text-[11px] text-slate-400">Showing everything in this section</span>
            )}
          </div>
        </div>
      )}

      <div className={refreshing ? 'opacity-40 pointer-events-none transition-opacity' : 'transition-opacity'}>
      {placeholder && isEmpty ? (
        <div className="p-6 bg-slate-50 text-center text-sm text-slate-500 italic"
             data-testid={`section-placeholder-${data.section}`}>
          {placeholder}
        </div>
      ) : (
        <>
          {/* Row 1 — Funnel volume */}
          <div className="p-4 md:p-5 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4 border-b border-slate-100">
            <MBlock label="Page Views"
              value={fmtNumIN(data.page_views)}
              formula={`page_view events where geo ${isInternational ? '≠ India' : isDoctor ? 'has doctor_ref' : '= India'} ∧ event ∈ period`}
              source="page_views"
              note={data.page_views === 0 ? 'Awaiting website team integration — see handover doc.' : '⏱ Filtered by event timestamp.'}
            />
            <MBlock label="Unique Visitors"
              value={fmtNumIN(data.unique_visitors)}
              formula="count(distinct visitor_id) within section page_views"
              source="page_views"
              note="⏱ Filtered by event timestamp."
            />
            <MBlock label="Total Leads"
              value={fmtNumIN(data.total_leads)}
              formula="V35.2 bucketing: non-INR payment OR non-+91 phone → International, else India"
              source="leads"
              note={`${FILTER_BY_CREATED}${(selAccounts.length || selCampaigns.length) ? '\n\nAd Account / Campaign filter: only leads that carry a campaign UTM can be traced back to an ad account. Leads with no campaign tag (older or organic traffic) are excluded from this count — which is why it drops sharply versus the unfiltered view.' : ''}${selWebsites.length ? '\n\nWebsite filter: leads are bucketed by their landing page URL; untagged leads sit in "Others".' : ''}`}
              onClick={onViewLeads ? openModal('Total Leads', { metric: 'total_leads' }) : undefined}
            />
            <MBlock label="Trials (Marketing)"
              value={fmtNumIN(data.trial_paid_marketing)}
              formula="trials where payment_created_by ∉ pre_sales (and notes.source ≠ quick_payment_link)"
              source="payments + users"
              note={`Drives CPL Marketing. ${FILTER_BY_CAPTURED}`}
              onClick={onViewLeads ? openModal('Trials (Marketing)', { metric: 'trials_marketing' }) : undefined}
            />
            <MBlock label="Trials (Pre-Sales)"
              value={fmtNumIN(data.trial_paid_presales)}
              formula="trials where payment_created_by ∈ pre_sales OR notes.source = quick_payment_link"
              source="payments + users"
              note={FILTER_BY_CAPTURED}
              muted
              onClick={onViewLeads ? openModal('Trials (Pre-Sales)', { metric: 'trials_presales' }) : undefined}
            />
            <MBlock label="New Enrolments"
              value={fmtNumIN(data.first_enrolments ?? data.enrolments)}
              formula="Unique leads whose lifetime-first enrolment payment landed in period. Renewals NOT counted."
              source="payments"
              note={FILTER_BY_CAPTURED}
              onClick={onViewLeads ? openModal('New Enrolments', { metric: 'first_enrolments' }) : undefined}
              highlight={(data.first_enrolments ?? data.enrolments) > 0}
            />
          </div>

          {/* Row 2 — Renewals + Revenue (V36 — first vs renewal split + FX-equivalent) */}
          <div className="p-4 md:p-5 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4 border-b border-slate-100">
            <MBlock label="Renewals"
              value={fmtNumIN(data.renewals || 0)}
              formula="Enrolment payments in period where lead already had an earlier enrolment in lifetime (or payment_type='renewal')."
              source="payments"
              note={`Counted as events, not leads (a lead may renew multiple times). ${FILTER_BY_CAPTURED}`}
              onClick={onViewLeads ? openModal('Renewals', { metric: 'renewals' }) : undefined}
              muted={data.renewals === 0}
            />
            <MBlock label="Sales Revenue (INR)"
              value={fmtINR(isInternational
                ? (data.sales_revenue_inr_equivalent || 0)
                : (salesByCurrency.INR?.sum || 0))}
              formula={isInternational
                ? "Σ (each currency × FX→INR). INR pass-through. Includes first enrolments + renewals."
                : "Σ enrolment payments · currency = INR · captured_at ∈ period"}
              source="payments"
              note={isInternational
                ? (fxBreakdownText ? `${fxBreakdownText}.  ${FILTER_BY_CAPTURED}` : `Total INR-equivalent across all currencies. ${FILTER_BY_CAPTURED}`)
                : `${salesByCurrency.INR?.count || 0} INR enrolment payments. ${FILTER_BY_CAPTURED}`}
              highlight
              onClick={onViewLeads ? openModal('Sales Revenue (INR)', { metric: 'sales_revenue_inr' }) : undefined}
            />
            <MBlock label="New Sales Revenue (INR)"
              value={fmtINR(isInternational
                ? (data.first_enrol_revenue_inr_equivalent || 0)
                : (firstEnrolByCurrency.INR?.sum || 0))}
              formula={isInternational
                ? "Σ (first-enrolment revenue per currency × FX→INR). New enrolments only — renewals excluded."
                : "Σ first-enrolment payments · currency = INR · captured_at ∈ period. New enrolments only — renewals excluded."}
              source="payments"
              note={isInternational
                ? `Revenue from new enrolments, INR-equivalent across all currencies. ${FILTER_BY_CAPTURED}`
                : `${firstEnrolByCurrency.INR?.count || 0} INR new-enrolment payments. ${FILTER_BY_CAPTURED}`}
              highlight
              onClick={onViewLeads ? openModal('New Sales Revenue (INR)', { metric: 'first_enrolments' }) : undefined}
            />
            <MBlock label="Trial Revenue (INR)"
              value={fmtINR(trialByCurrency.INR?.sum || 0)}
              formula="Σ trial payments · currency = INR · amounts ∈ {99, 199, 289, 298}"
              source="payments"
              note={`${trialByCurrency.INR?.count || 0} INR trial payments. ${FILTER_BY_CAPTURED}`}
            />
            <MBlock label="Renewal Revenue (INR)"
              value={fmtINR(isInternational
                ? (data.renewal_revenue_inr_equivalent || 0)
                : (renewalByCurrency.INR?.sum || 0))}
              formula={isInternational
                ? "Σ (renewal revenue per currency × FX→INR). All renewals, INR-equivalent."
                : "Σ renewal-classified payments · currency = INR"}
              source="payments"
              note={isInternational
                ? `Renewals across all currencies, converted to INR. ${FILTER_BY_CAPTURED}`
                : `${renewalByCurrency.INR?.count || 0} INR renewal payments. ${FILTER_BY_CAPTURED}`}
              muted={isInternational
                ? !(data.renewal_revenue_inr_equivalent)
                : !(renewalByCurrency.INR?.sum)}
            />
            {/* V81 — Refunds. Every revenue figure above is already NET of these:
                a partial refund takes off only the refunded part, a full refund
                also removes the enrolment from the counts. */}
            <MBlock label="Refunds (INR)"
              value={data.refunds_inr_equivalent ? `−${fmtINR(data.refunds_inr_equivalent)}` : fmtINR(0)}
              formula="Σ refunds issued in the period (all currencies × FX→INR), by refund date — not by the date the original payment was captured."
              source="payments.refunds[] (Razorpay webhook + hourly reconciliation)"
              note={`${data.refunds_count || 0} refunded payment(s) in this period. Sales / Trial / Renewal revenue above is already net of this. Filtered by the date the refund was issued.${(data.refunds_fx_breakdown || []).length > 1 ? '\n\n' + (data.refunds_fx_breakdown || []).map(b => `• ${b.currency} ${b.native_amount} → ₹${b.inr_equivalent}`).join('\n') : ''}`}
              onClick={onViewLeads ? openModal('Refunds', { metric: 'refunds' }) : undefined}
              muted={!(data.refunds_count)}
            />
            <MBlock label="Refunded Enrolments"
              value={fmtNumIN(data.refunded_enrolments || 0)}
              formula="Unique leads whose enrolment was FULLY refunded in the period."
              source="payments + leads"
              note={`These leads are NOT counted in Enrolments, New Enrolments or Renewals, their status is set to Refunded, and LMS is told to revoke every batch and course. ${data.refunded_trials ? `${data.refunded_trials} refunded trial payment(s) in the same period.` : ''}`}
              onClick={onViewLeads ? openModal('Refunds', { metric: 'refunds' }) : undefined}
              muted={!(data.refunded_enrolments)}
            />
            {/* V35.2 + V36 — USD cards: ONLY in International section, never Domestic */}
            {isInternational && (
              <>
                <MBlock label="Sales Revenue (USD)"
                  value={`$${(salesByCurrency.USD?.sum || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  formula="Σ enrolment payments · currency = USD. Includes first enrolments + renewals."
                  source="payments"
                  note={`${salesByCurrency.USD?.count || 0} USD enrolment payments (first + renewal). ${FILTER_BY_CAPTURED}`}
                  highlight={(salesByCurrency.USD?.sum || 0) > 0}
                  onClick={onViewLeads ? openModal('Sales Revenue (USD)', { metric: 'sales_revenue_usd' }) : undefined}
                />
                <MBlock label="New Sales Revenue (USD)"
                  value={`$${(firstEnrolByCurrency.USD?.sum || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  formula="Σ first-enrolment payments · currency = USD. New enrolments only — renewals excluded."
                  source="payments"
                  note={`${firstEnrolByCurrency.USD?.count || 0} USD new-enrolment payments. ${FILTER_BY_CAPTURED}`}
                  highlight={(firstEnrolByCurrency.USD?.sum || 0) > 0}
                  onClick={onViewLeads ? openModal('New Sales Revenue (USD)', { metric: 'first_enrolments' }) : undefined}
                />
                <MBlock label="Trial Revenue (USD)"
                  value={`$${(trialByCurrency.USD?.sum || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  formula="Σ trial payments · currency = USD · amounts ∈ {10, 20}"
                  source="payments"
                  note={`${trialByCurrency.USD?.count || 0} USD trial payments. ${FILTER_BY_CAPTURED}`}
                />
                <MBlock label="Renewal Revenue (USD)"
                  value={`$${(renewalByCurrency.USD?.sum || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  formula="Σ renewal-classified payments · currency = USD"
                  source="payments"
                  note={`${renewalByCurrency.USD?.count || 0} USD renewal payments. ${FILTER_BY_CAPTURED}`}
                  muted={!(renewalByCurrency.USD?.sum)}
                />
                {/* V35.2 — Non-INR, non-USD currencies: ONE combined Revenue card each (dynamic) */}
                {extraCurrencies.map((curr) => {
                  const symMap = { AUD: 'A$', EUR: '€', GBP: '£', CAD: 'C$', SGD: 'S$', AED: 'AED ', NZD: 'NZ$' };
                  const sym = symMap[curr] || `${curr} `;
                  return (
                    <MBlock key={curr} label={`Revenue (${curr})`}
                      value={`${sym}${(allRevByCurrency[curr]?.sum || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                      formula={`Σ ALL payments (trial + sales) · currency = ${curr}`}
                      source="payments"
                      note={`${allRevByCurrency[curr]?.count || 0} ${curr} payments. ${FILTER_BY_CAPTURED}`}
                    />
                  );
                })}
                {/* V36 — Non-INR, non-USD renewals as separate cards if present */}
                {extraRenewalCurrencies.map((curr) => {
                  const symMap = { AUD: 'A$', EUR: '€', GBP: '£', CAD: 'C$', SGD: 'S$', AED: 'AED ', NZD: 'NZ$' };
                  const sym = symMap[curr] || `${curr} `;
                  return (
                    <MBlock key={`renew-${curr}`} label={`Renewal Revenue (${curr})`}
                      value={`${sym}${(renewalByCurrency[curr]?.sum || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                      formula={`Σ renewal-classified payments · currency = ${curr}`}
                      source="payments"
                      note={`${renewalByCurrency[curr]?.count || 0} ${curr} renewal payments. ${FILTER_BY_CAPTURED}`}
                    />
                  );
                })}
              </>
            )}
          </div>

          {/* Row 3 — Spend + Efficiency (all INR) */}
          <div className="p-4 md:p-5 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4 border-b border-slate-100">
            <MBlock label="Ad Spend (INR)"
              value={fmtINR(data.ad_spend_inr)}
              formula={`Σ Meta ad-account spend → ${data.section} (always INR billed)`}
              source="campaigns (Meta API)"
              note={`${adAcctTooltip}${selAccounts.length ? `\n\nNarrowed to ad account(s): ${selAccounts.join(', ')}` : ''}${selCampaigns.length ? `\n\nNarrowed to campaign(s): ${selCampaigns.join(', ')}` : ''}${selWebsites.length ? '\n\nNote: the Website selector does NOT narrow ad spend — Meta reports spend per campaign, never per landing page.' : ''}`}
            />
            <MBlock label="ROAS"
              value={`${(data.roas_inr_equivalent || 0).toFixed(2)}x`}
              formula={isInternational
                ? "(INR sales + USD×95 + AUD×68 + EUR×110 + GBP×128 + …) / INR Spend"
                : "INR Sales Revenue / INR Spend"}
              source="payments + campaigns"
              note={`Revenue INR-equiv: ${fmtINR(data.sales_revenue_inr_equivalent)} · Goal ≥ 1.0x. ${FILTER_BY_CAPTURED}`}
              highlight={data.roas_inr_equivalent >= 1}
              warn={data.roas_inr_equivalent > 0 && data.roas_inr_equivalent < 1}
            />
            <MBlock label="CPL (Marketing)"
              value={fmtINR(data.cpl_marketing_inr)}
              formula="INR Ad Spend / Marketing Trials"
              source="campaigns + payments"
              note={`Cost to acquire a Marketing-attributed trial. ${FILTER_BY_CAPTURED}`}
            />
            <MBlock label="Cost / Trial"
              value={fmtINR(data.cost_per_trial_inr)}
              formula="INR Ad Spend / (Marketing Trials + Pre-Sales Trials)"
              source="campaigns + payments"
              note={`Blended — includes Pre-Sales-assisted trials. ${FILTER_BY_CAPTURED}`}
            />
            <MBlock label="Cost / Enrolment"
              value={fmtINR(data.cost_per_enrolment_inr)}
              formula="INR Ad Spend / Enrolments (unique leads in period)"
              source="campaigns + payments"
              note={FILTER_BY_CAPTURED}
            />
            <MBlock label="Avg Enrolment Value"
              value={fmtINR(data.avg_enrolment_value_inr)}
              formula="INR First-Enrolment Revenue / First Enrolments (renewals excluded)"
              source="payments"
              note={FILTER_BY_CAPTURED}
            />
          </div>

          {/* Row 4 — Conversion + Time */}
          <div className="p-4 md:p-5 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            <MBlock label="Sales Conversion"
              value={`${(data.sales_conversion_pct || 0).toFixed(2)}%`}
              formula="New Enrolments ÷ Paid Trials (Marketing + Pre-Sales) × 100"
              source="payments"
              note="Section-scoped. How many paid-trial customers went on to enrol."
            />
            <MBlock label="Checkout Conversion"
              value={`${(data.checkout_conversion_pct || 0).toFixed(2)}%`}
              formula="Total Leads / Unique Visitors × 100"
              source="leads + page_views"
              note="Form-fill rate."
            />
            <MBlock label="Avg Time to Convert"
              value={`${data.avg_time_to_convert_days_median ?? 0}d (median)`}
              formula="median(enrolled_at − earliest_trial_paid_at) per lead"
              source="payments"
              note={`Mean: ${data.avg_time_to_convert_days_mean ?? 0}d · n=${data.avg_time_to_convert_sample_size ?? 0}`}
            />
          </div>
        </>
      )}
      </div>
    </section>
  );
};

// V35 — Compact metric tile used inside SectionBlock. Always exposes a MetricTooltip.
const MBlock = ({ label, value, formula, source, note, onClick, highlight, warn, muted }) => {
  const bg = muted ? 'bg-slate-50/60 border-slate-200/70'
    : highlight ? 'bg-emerald-50/60 border-emerald-200'
    : warn ? 'bg-rose-50/60 border-rose-200'
    : 'bg-white border-slate-200';
  return (
    <div className={`rounded-lg border ${bg} p-3 min-w-0 ${onClick ? 'cursor-pointer hover:shadow-sm transition-shadow' : ''}`}
         onClick={onClick}
         data-testid={`mblock-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium flex items-center">
        <span className="truncate">{label}</span>
        <MetricTooltip formula={formula} source={source} note={note}
          testId={`mblock-tooltip-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`} />
      </div>
      <div className="text-base md:text-lg font-bold text-slate-900 mt-0.5 truncate" title={value}>{value}</div>
      {onClick && <span className="text-[10px] text-indigo-600 font-medium mt-0.5 block">View Leads →</span>}
    </div>
  );
};



// Quality Bar Component
const QualityBar = ({ label, count, total, color }) => {
  const percentage = total > 0 ? (count / total * 100) : 0;
  const colorClasses = {
    emerald: 'bg-emerald-500',
    amber: 'bg-amber-500',
    red: 'bg-red-500',
    slate: 'bg-slate-300'
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm text-slate-600">{label}</span>
        <span className="text-sm text-slate-900 font-medium">{count} ({percentage.toFixed(1)}%)</span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div 
          className={`h-full ${colorClasses[color]} transition-all duration-500`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

// Campaign Modal
const CampaignModal = ({ onClose, onSave }) => {
  const [formData, setFormData] = useState({
    campaign_name: '',
    source: '',
    medium: '',
    country: '',
    currency: 'INR',
    notes: ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const params = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      
      await axios.post(
        `${API_URL}/api/marketing/campaigns?${params}`,
        {},
        { withCredentials: true }
      );
      toast.success('Campaign created');
      onSave();
    } catch (error) {
      toast.error('Failed to create campaign');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
        <div className="p-6 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900">Add New Campaign</h3>
          <p className="text-sm text-slate-500">Create a campaign to track ad spend</p>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Campaign Name *</label>
            <input
              type="text"
              required
              value={formData.campaign_name}
              onChange={(e) => setFormData({ ...formData, campaign_name: e.target.value })}
              className="input-base"
              placeholder="e.g., FB_India_March_2026"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Source</label>
              <select
                value={formData.source}
                onChange={(e) => setFormData({ ...formData, source: e.target.value })}
                className="input-base"
              >
                <option value="">Select</option>
                <option value="Facebook">Facebook</option>
                <option value="Instagram">Instagram</option>
                <option value="Google">Google</option>
                <option value="YouTube">YouTube</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Medium</label>
              <select
                value={formData.medium}
                onChange={(e) => setFormData({ ...formData, medium: e.target.value })}
                className="input-base"
              >
                <option value="">Select</option>
                <option value="cpc">CPC</option>
                <option value="cpm">CPM</option>
                <option value="organic">Organic</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Country</label>
              <input
                type="text"
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                className="input-base"
                placeholder="e.g., India"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Currency</label>
              <select
                value={formData.currency}
                onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                className="input-base"
              >
                <option value="INR">INR</option>
                <option value="USD">USD</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="input-base"
              rows="2"
              placeholder="Campaign notes..."
            />
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary">
              {saving ? 'Creating...' : 'Create Campaign'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Spend Modal
const SpendModal = ({ campaign, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    amount: '',
    currency: campaign.currency || 'INR',
    notes: ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const params = new URLSearchParams();
      params.append('date', formData.date);
      params.append('amount', formData.amount);
      params.append('currency', formData.currency);
      if (formData.notes) params.append('notes', formData.notes);
      
      await axios.post(
        `${API_URL}/api/marketing/campaigns/${campaign.campaign_id}/spend?${params}`,
        {},
        { withCredentials: true }
      );
      toast.success('Spend added');
      onSave();
    } catch (error) {
      toast.error('Failed to add spend');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
        <div className="p-6 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900">Add Spend</h3>
          <p className="text-sm text-slate-500">{campaign.campaign_name}</p>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Date *</label>
            <input
              type="date"
              required
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              className="input-base"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Amount *</label>
              <input
                type="number"
                required
                min="0"
                step="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                className="input-base"
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Currency</label>
              <select
                value={formData.currency}
                onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                className="input-base"
              >
                <option value="INR">INR</option>
                <option value="USD">USD</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
            <input
              type="text"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="input-base"
              placeholder="Optional notes..."
            />
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary">
              {saving ? 'Adding...' : 'Add Spend'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Creatives Tab Component
const CreativesTab = ({ topCreatives, filters, formatCurrency, formatNumber, onRefresh, currentUser }) => {
  const [sortBy, setSortBy] = useState('total_spend');
  const [sortOrder, setSortOrder] = useState('desc');
  const [expandedCreative, setExpandedCreative] = useState(null);
  const [clearing, setClearing] = useState(false);

  // Sort creatives locally for instant feedback
  const sortedCreatives = [...topCreatives].sort((a, b) => {
    const aVal = a[sortBy] || 0;
    const bVal = b[sortBy] || 0;
    return sortOrder === 'desc' ? bVal - aVal : aVal - bVal;
  });

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const clearAllCreatives = async () => {
    if (!window.confirm('Are you sure you want to delete ALL creative/ad data? This action cannot be undone.')) {
      return;
    }
    
    setClearing(true);
    try {
      const response = await axios.delete(
        `${API_URL}/api/marketing/creatives/clear-all`,
        { withCredentials: true }
      );
      toast.success(response.data.message || 'All creatives cleared');
      onRefresh();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to clear creatives';
      toast.error(typeof errorMsg === 'object' ? JSON.stringify(errorMsg) : errorMsg);
    } finally {
      setClearing(false);
    }
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return <ChevronDown className="w-3 h-3 opacity-30" />;
    return sortOrder === 'desc' 
      ? <ChevronDown className="w-3 h-3" /> 
      : <ChevronRight className="w-3 h-3 rotate-90" />;
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      active: 'bg-green-100 text-green-700',
      not_delivering: 'bg-slate-100 text-slate-600',
      paused: 'bg-amber-100 text-amber-700',
      deleted: 'bg-red-100 text-red-700'
    };
    return statusColors[status] || 'bg-slate-100 text-slate-600';
  };

  return (
    <div className="space-y-4">
      {/* Summary Cards — funnel + ROI (attributed to creatives) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="card">
          <div className="card-body py-3">
            <div className="text-xs text-slate-500">Total Ads</div>
            <div className="text-lg font-bold text-slate-900">{topCreatives.filter(c => !c.is_unmatched).length}</div>
          </div>
        </div>
        <div className="card">
          <div className="card-body py-3">
            <div className="text-xs text-slate-500">Total Spend</div>
            <div className="text-lg font-bold text-slate-900">
              {formatCurrency(topCreatives.reduce((sum, c) => sum + (c.total_spend || 0), 0))}
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-body py-3">
            <div className="text-xs text-slate-500">Leads</div>
            <div className="text-lg font-bold text-slate-900">
              {formatNumber(topCreatives.reduce((sum, c) => sum + (c.leads || 0), 0))}
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-body py-3">
            <div className="text-xs text-slate-500">Paid Trials</div>
            <div className="text-lg font-bold text-slate-900">
              {formatNumber(topCreatives.reduce((sum, c) => sum + (c.paid_trials || 0), 0))}
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-body py-3">
            <div className="text-xs text-slate-500">Enrolments</div>
            <div className="text-lg font-bold text-emerald-600">
              {formatNumber(topCreatives.reduce((sum, c) => sum + (c.enrolments || 0), 0))}
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-body py-3">
            <div className="text-xs text-slate-500">Revenue (₹, FX-adj)</div>
            <div className="text-lg font-bold text-emerald-600">
              ₹{formatNumber(Math.round(topCreatives.reduce((sum, c) => sum + (c.revenue_inr || 0), 0)))}
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-body py-3">
            <div className="text-xs text-slate-500">Blended ROAS</div>
            <div className="text-lg font-bold text-slate-900">
              {(() => {
                const spend = topCreatives.reduce((s, c) => s + (c.total_spend || 0), 0);
                const rev = topCreatives.reduce((s, c) => s + (c.revenue_inr || 0), 0);
                return spend > 0 ? (rev / spend).toFixed(2) + 'x' : '—';
              })()}
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-body py-3">
            <div className="text-xs text-slate-500">Cost / Enrolment</div>
            <div className="text-lg font-bold text-slate-900">
              {(() => {
                const spend = topCreatives.reduce((s, c) => s + (c.total_spend || 0), 0);
                const enr = topCreatives.reduce((s, c) => s + (c.enrolments || 0), 0);
                return enr > 0 ? formatCurrency(spend / enr) : '—';
              })()}
            </div>
          </div>
        </div>
      </div>

      {/* Creatives Table */}
      <div className="card">
        <div className="card-header flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-slate-900">Ad Creative Performance</h3>
            <p className="text-sm text-slate-500">
              Ad metrics + funnel attribution (leads → trials → enrolments → ₹ revenue) • Sorted by {sortBy.replace('_', ' ')}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {currentUser?.role === 'super_admin' && topCreatives.length > 0 && (
              <button
                onClick={clearAllCreatives}
                disabled={clearing}
                className="text-xs text-red-600 hover:text-red-800 flex items-center gap-1"
                title="Clear all creative data"
              >
                <Trash2 className={`w-3 h-3 ${clearing ? 'animate-pulse' : ''}`} />
                Clear All
              </button>
            )}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="creatives-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="p-3 text-left text-xs font-medium text-slate-500">#</th>
                <th className="p-3 text-left text-xs font-medium text-slate-500 min-w-[250px]">Ad Name</th>
                <th className="p-3 text-left text-xs font-medium text-slate-500">Ad Set</th>
                <th className="p-3 text-left text-xs font-medium text-slate-500">Campaign</th>
                <th className="p-3 text-center text-xs font-medium text-slate-500">Status</th>
                <th 
                  className="p-3 text-right text-xs font-medium text-slate-500 cursor-pointer hover:bg-slate-100"
                  onClick={() => handleSort('total_spend')}
                >
                  <div className="flex items-center justify-end gap-1">
                    Spend <SortIcon field="total_spend" />
                  </div>
                </th>
                <th
                  className="p-3 text-right text-xs font-medium text-slate-500 cursor-pointer hover:bg-slate-100 bg-emerald-50/40"
                  onClick={() => handleSort('leads')}
                >
                  <div className="flex items-center justify-end gap-1">
                    Leads <SortIcon field="leads" />
                  </div>
                </th>
                <th
                  className="p-3 text-right text-xs font-medium text-slate-500 cursor-pointer hover:bg-slate-100 bg-emerald-50/40"
                  onClick={() => handleSort('paid_trials')}
                >
                  <div className="flex items-center justify-end gap-1">
                    Trials <SortIcon field="paid_trials" />
                  </div>
                </th>
                <th
                  className="p-3 text-right text-xs font-medium text-slate-500 cursor-pointer hover:bg-slate-100 bg-emerald-50/40"
                  onClick={() => handleSort('enrolments')}
                >
                  <div className="flex items-center justify-end gap-1">
                    Enrols <SortIcon field="enrolments" />
                  </div>
                </th>
                <th
                  className="p-3 text-right text-xs font-medium text-slate-500 cursor-pointer hover:bg-slate-100 bg-emerald-50/40"
                  onClick={() => handleSort('revenue_inr')}
                >
                  <div className="flex items-center justify-end gap-1">
                    Revenue (₹) <SortIcon field="revenue_inr" />
                  </div>
                </th>
                <th
                  className="p-3 text-right text-xs font-medium text-slate-500 cursor-pointer hover:bg-slate-100 bg-emerald-50/40"
                  onClick={() => handleSort('roas_inr')}
                >
                  <div className="flex items-center justify-end gap-1">
                    ROAS <SortIcon field="roas_inr" />
                  </div>
                </th>
                <th 
                  className="p-3 text-right text-xs font-medium text-slate-500 cursor-pointer hover:bg-slate-100"
                  onClick={() => handleSort('total_results')}
                >
                  <div className="flex items-center justify-end gap-1">
                    Results <SortIcon field="total_results" />
                  </div>
                </th>
                <th 
                  className="p-3 text-right text-xs font-medium text-slate-500 cursor-pointer hover:bg-slate-100"
                  onClick={() => handleSort('cost_per_result')}
                >
                  <div className="flex items-center justify-end gap-1">
                    CPR <SortIcon field="cost_per_result" />
                  </div>
                </th>
                <th 
                  className="p-3 text-right text-xs font-medium text-slate-500 cursor-pointer hover:bg-slate-100"
                  onClick={() => handleSort('impressions')}
                >
                  <div className="flex items-center justify-end gap-1">
                    Impressions <SortIcon field="impressions" />
                  </div>
                </th>
                <th 
                  className="p-3 text-right text-xs font-medium text-slate-500 cursor-pointer hover:bg-slate-100"
                  onClick={() => handleSort('reach')}
                >
                  <div className="flex items-center justify-end gap-1">
                    Reach <SortIcon field="reach" />
                  </div>
                </th>
                <th 
                  className="p-3 text-right text-xs font-medium text-slate-500 cursor-pointer hover:bg-slate-100"
                  onClick={() => handleSort('link_clicks')}
                >
                  <div className="flex items-center justify-end gap-1">
                    Clicks <SortIcon field="link_clicks" />
                  </div>
                </th>
                <th 
                  className="p-3 text-right text-xs font-medium text-slate-500 cursor-pointer hover:bg-slate-100"
                  onClick={() => handleSort('ctr')}
                >
                  <div className="flex items-center justify-end gap-1">
                    CTR <SortIcon field="ctr" />
                  </div>
                </th>
                <th 
                  className="p-3 text-right text-xs font-medium text-slate-500 cursor-pointer hover:bg-slate-100"
                  onClick={() => handleSort('cpm')}
                >
                  <div className="flex items-center justify-end gap-1">
                    CPM <SortIcon field="cpm" />
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedCreatives.length === 0 ? (
                <tr>
                  <td colSpan="18" className="p-8 text-center text-slate-500">
                    <div className="flex flex-col items-center gap-2">
                      <BarChart3 className="w-8 h-8 text-slate-300" />
                      <p>No creative data available yet.</p>
                      <p className="text-xs text-slate-400">
                        Upload a Meta Ads report CSV with ad-level data to see performance breakdowns.
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                sortedCreatives.map((creative, idx) => (
                  <tr 
                    key={creative.creative_id || idx} 
                    className={`border-b border-slate-100 hover:bg-slate-50 cursor-pointer ${creative.is_unmatched ? 'bg-amber-50/60 border-l-2 border-l-amber-400' : ''}`}
                    data-testid={creative.is_unmatched ? 'creative-row-unmatched' : `creative-row-${idx}`}
                    onClick={() => setExpandedCreative(expandedCreative === idx ? null : idx)}
                  >
                    <td className="p-3 text-sm text-slate-500">{idx + 1}</td>
                    <td className="p-3">
                      <div className="flex flex-col">
                        <div 
                          className="font-medium text-slate-900 max-w-[250px] truncate" 
                          title={creative.ad_name}
                        >
                          {creative.ad_name}
                        </div>
                        {creative.result_type && (
                          <div className="text-xs text-slate-400 mt-0.5">
                            {creative.result_type}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="p-3">
                      <div 
                        className="text-sm text-slate-600 max-w-[150px] truncate" 
                        title={creative.ad_set_name}
                      >
                        {creative.ad_set_name || '-'}
                      </div>
                    </td>
                    <td className="p-3">
                      <div 
                        className="text-sm text-slate-600 max-w-[150px] truncate" 
                        title={creative.campaign_name}
                      >
                        {creative.campaign_name || '-'}
                      </div>
                    </td>
                    <td className="p-3 text-center">
                      <span className={`px-2 py-0.5 text-xs rounded-full ${getStatusBadge(creative.delivery_status)}`}>
                        {creative.delivery_status === 'not_delivering' ? 'Off' : 
                         creative.delivery_status === 'active' ? 'Active' : 
                         creative.delivery_status || '-'}
                      </span>
                    </td>
                    <td className="p-3 text-right text-sm font-medium text-slate-900">
                      {formatCurrency(creative.total_spend || 0)}
                    </td>
                    <td className="p-3 text-right text-sm text-slate-700 bg-emerald-50/40">
                      {formatNumber(creative.leads || 0)}
                    </td>
                    <td className="p-3 text-right text-sm text-slate-700 bg-emerald-50/40">
                      {formatNumber(creative.paid_trials || 0)}
                    </td>
                    <td className="p-3 text-right text-sm font-semibold text-emerald-700 bg-emerald-50/40">
                      {formatNumber(creative.enrolments || 0)}
                    </td>
                    <td className="p-3 text-right text-sm font-semibold text-emerald-700 bg-emerald-50/40">
                      ₹{formatNumber(Math.round(creative.revenue_inr || 0))}
                    </td>
                    <td className="p-3 text-right text-sm bg-emerald-50/40">
                      <span className={(creative.roas_inr || 0) >= 1 ? 'text-green-600 font-semibold' : 'text-slate-500'}>
                        {(creative.roas_inr || 0).toFixed(2)}x
                      </span>
                    </td>
                    <td className="p-3 text-right text-sm font-semibold text-indigo-600">
                      {creative.total_results || 0}
                    </td>
                    <td className="p-3 text-right text-sm">
                      <span className={creative.cost_per_result < 400 ? 'text-green-600' : 'text-slate-600'}>
                        {formatCurrency(creative.cost_per_result || 0)}
                      </span>
                    </td>
                    <td className="p-3 text-right text-sm text-slate-600">
                      {formatNumber(creative.impressions || 0)}
                    </td>
                    <td className="p-3 text-right text-sm text-slate-600">
                      {formatNumber(creative.reach || 0)}
                    </td>
                    <td className="p-3 text-right text-sm text-slate-600">
                      {formatNumber(creative.link_clicks || 0)}
                    </td>
                    <td className="p-3 text-right text-sm">
                      <span className={creative.ctr > 1 ? 'text-green-600 font-medium' : 'text-slate-600'}>
                        {creative.ctr?.toFixed(2) || '0.00'}%
                      </span>
                    </td>
                    <td className="p-3 text-right text-sm text-slate-600">
                      {formatCurrency(creative.cpm || 0)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Table Footer */}
        {sortedCreatives.length > 0 && (
          <div className="p-3 border-t border-slate-200 bg-slate-50 text-xs text-slate-500">
            Showing {sortedCreatives.length} ads • Data from uploaded Meta Ads reports
          </div>
        )}
      </div>
    </div>
  );
};

// Lead Quality Tab Component
const LeadQualityTab = ({ filters, formatCurrency, formatNumber }) => {
  const [qualityData, setQualityData] = useState([]);
  const [groupBy, setGroupBy] = useState('campaign');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchQualityData();
  }, [filters, groupBy]);

  const fetchQualityData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      params.append('group_by', groupBy);

      const response = await axios.get(
        `${API_URL}/api/marketing/lead-quality-by-source?${params}`,
        { withCredentials: true }
      );
      setQualityData(response.data.data || []);
    } catch (error) {
      console.error('Error fetching lead quality data:', error);
      toast.error('Failed to load lead quality data');
    } finally {
      setLoading(false);
    }
  };

  // Calculate totals
  const totals = qualityData.reduce((acc, item) => ({
    total_leads: acc.total_leads + (item.total_leads || 0),
    good_leads: acc.good_leads + (item.good_leads || 0),
    avg_leads: acc.avg_leads + (item.avg_leads || 0),
    bad_leads: acc.bad_leads + (item.bad_leads || 0),
    not_rated: acc.not_rated + (item.not_rated || 0),
    converted: acc.converted + (item.converted || 0),
    revenue: acc.revenue + (item.revenue || 0)
  }), { total_leads: 0, good_leads: 0, avg_leads: 0, bad_leads: 0, not_rated: 0, converted: 0, revenue: 0 });

  const overallQualityScore = (totals.good_leads + totals.avg_leads + totals.bad_leads) > 0
    ? Math.round((totals.good_leads / (totals.good_leads + totals.avg_leads + totals.bad_leads)) * 100)
    : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="lead-quality-tab">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <div className="card">
          <div className="card-body py-3">
            <div className="text-xs text-slate-500">Total Leads</div>
            <div className="text-lg font-bold text-slate-900">{formatNumber(totals.total_leads)}</div>
          </div>
        </div>
        <div className="card">
          <div className="card-body py-3">
            <div className="text-xs text-slate-500">Good Leads</div>
            <div className="text-lg font-bold text-emerald-600">{totals.good_leads}</div>
          </div>
        </div>
        <div className="card">
          <div className="card-body py-3">
            <div className="text-xs text-slate-500">Average Leads</div>
            <div className="text-lg font-bold text-amber-600">{totals.avg_leads}</div>
          </div>
        </div>
        <div className="card">
          <div className="card-body py-3">
            <div className="text-xs text-slate-500">Bad Leads</div>
            <div className="text-lg font-bold text-red-600">{totals.bad_leads}</div>
          </div>
        </div>
        <div className="card">
          <div className="card-body py-3">
            <div className="text-xs text-slate-500">Quality Score</div>
            <div className={`text-lg font-bold ${
              overallQualityScore >= 50 ? 'text-emerald-600' : 
              overallQualityScore >= 25 ? 'text-amber-600' : 'text-red-600'
            }`}>
              {overallQualityScore}%
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-body py-3">
            <div className="text-xs text-slate-500">Converted</div>
            <div className="text-lg font-bold text-indigo-600">{totals.converted}</div>
          </div>
        </div>
      </div>

      {/* Group By Selector */}
      <div className="flex items-center gap-4">
        <label className="text-sm font-medium text-slate-700">Group by:</label>
        <div className="flex gap-2">
          {[
            { value: 'campaign', label: 'Campaign' },
            { value: 'creative', label: 'Creative/Ad' },
            { value: 'source', label: 'Source' }
          ].map(option => (
            <button
              key={option.value}
              onClick={() => setGroupBy(option.value)}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                groupBy === option.value
                  ? 'bg-indigo-600 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Quality Breakdown Table */}
      <div className="card">
        <div className="card-header">
          <h3 className="font-semibold text-slate-900">Lead Quality by {groupBy === 'creative' ? 'Creative/Ad' : groupBy.charAt(0).toUpperCase() + groupBy.slice(1)}</h3>
          <p className="text-sm text-slate-500">
            See which {groupBy === 'creative' ? 'ads' : 'campaigns'} are bringing good vs bad leads
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="p-3 text-left text-xs font-medium text-slate-500">#</th>
                <th className="p-3 text-left text-xs font-medium text-slate-500">{groupBy === 'creative' ? 'Creative/Ad' : groupBy.charAt(0).toUpperCase() + groupBy.slice(1)}</th>
                <th className="p-3 text-right text-xs font-medium text-slate-500">Total</th>
                <th className="p-3 text-right text-xs font-medium text-slate-500 text-emerald-600">Good</th>
                <th className="p-3 text-right text-xs font-medium text-slate-500 text-amber-600">Avg</th>
                <th className="p-3 text-right text-xs font-medium text-slate-500 text-red-600">Bad</th>
                <th className="p-3 text-right text-xs font-medium text-slate-500">Not Rated</th>
                <th className="p-3 text-right text-xs font-medium text-slate-500">Quality %</th>
                <th className="p-3 text-right text-xs font-medium text-slate-500">Converted</th>
                <th className="p-3 text-right text-xs font-medium text-slate-500">Conv %</th>
                <th className="p-3 text-right text-xs font-medium text-slate-500">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {qualityData.length === 0 ? (
                <tr>
                  <td colSpan="11" className="p-8 text-center text-slate-500">
                    <div className="flex flex-col items-center gap-2">
                      <Users className="w-8 h-8 text-slate-300" />
                      <p>No lead quality data available yet.</p>
                      <p className="text-xs text-slate-400">
                        Rate your leads (good/average/bad) to see quality breakdown by {groupBy}.
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                qualityData.map((item, idx) => (
                  <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="p-3 text-sm text-slate-500">{idx + 1}</td>
                    <td className="p-3">
                      <div className="font-medium text-slate-900 max-w-[250px] truncate" title={item.name}>
                        {item.name}
                      </div>
                    </td>
                    <td className="p-3 text-right text-sm font-medium">{item.total_leads}</td>
                    <td className="p-3 text-right text-sm text-emerald-600 font-medium">{item.good_leads}</td>
                    <td className="p-3 text-right text-sm text-amber-600 font-medium">{item.avg_leads}</td>
                    <td className="p-3 text-right text-sm text-red-600 font-medium">{item.bad_leads}</td>
                    <td className="p-3 text-right text-sm text-slate-400">{item.not_rated}</td>
                    <td className="p-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <div className="w-16 h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${
                              item.quality_score >= 50 ? 'bg-emerald-500' : 
                              item.quality_score >= 25 ? 'bg-amber-500' : 'bg-red-500'
                            }`}
                            style={{ width: `${item.quality_score}%` }}
                          />
                        </div>
                        <span className={`text-sm font-medium ${
                          item.quality_score >= 50 ? 'text-emerald-600' : 
                          item.quality_score >= 25 ? 'text-amber-600' : 'text-red-600'
                        }`}>
                          {item.quality_score}%
                        </span>
                      </div>
                    </td>
                    <td className="p-3 text-right text-sm text-indigo-600 font-medium">{item.converted}</td>
                    <td className="p-3 text-right text-sm">{item.conversion_rate}%</td>
                    <td className="p-3 text-right text-sm font-medium text-emerald-600">
                      {formatCurrency(item.revenue)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {qualityData.length > 0 && (
          <div className="p-3 border-t border-slate-200 bg-slate-50 text-xs text-slate-500">
            Showing {qualityData.length} {groupBy === 'creative' ? 'creatives/ads' : groupBy + 's'} • Quality Score = % of Good Leads (excluding not rated)
          </div>
        )}
      </div>
    </div>
  );
};

export default MarketingDashboard;

// ============ SALES METRICS TAB ============
const SalesMetricsTab = ({ filters, openLeadsModal }) => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [productUrls, setProductUrls] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedCurrencies, setSelectedCurrencies] = useState([]);

  useEffect(() => {
    fetchProductUrls();
  }, []);

  useEffect(() => {
    fetchMetrics();
  }, [filters.start_date, filters.end_date, selectedProducts, selectedCurrencies]);

  const fetchProductUrls = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/marketing/product-urls`, { withCredentials: true });
      setProductUrls(res.data || []);
    } catch (err) {
      console.error('Failed to load product URLs:', err);
    }
  };

  const fetchMetrics = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (selectedProducts.length) params.append('product_url', selectedProducts.join(','));
      if (selectedCurrencies.length) params.append('currency', selectedCurrencies.join(','));

      const res = await axios.get(`${API_URL}/api/marketing/sales-metrics?${params}`, { withCredentials: true });
      setMetrics(res.data);
    } catch (err) {
      console.error('Failed to load sales metrics:', err);
      toast.error('Failed to load sales metrics');
    } finally {
      setLoading(false);
    }
  };

  const fmtNum = (n) => {
    if (n == null) return '0';
    return n.toLocaleString('en-IN');
  };

  const fmtCurrency = (amount, cur) => {
    if (amount == null) return cur === 'USD' ? '$0' : '₹0';
    const prefix = selectedCurrencies.length === 1 && selectedCurrencies[0] === 'USD' ? '$' : '₹';
    return prefix + amount.toLocaleString('en-IN', { maximumFractionDigits: 2 });
  };

  const fmtPct = (n) => {
    if (n == null) return '0%';
    return n.toFixed(2) + '%';
  };

  if (loading && !metrics) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
  }

  const v = metrics?.views || {};
  const s = metrics?.sales || {};
  const r = metrics?.revenue || {};
  const c = metrics?.conversions || {};

  return (
    <div className="space-y-5" data-testid="sales-metrics-tab">
      {/* Filters Row */}
      <div className="card">
        <div className="card-body py-3">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="w-4 h-4 text-slate-500" />
            <span className="text-sm font-medium text-slate-700">Sales Metrics Filters</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-slate-500 mb-1">Product (Landing Page)</label>
              <MultiSelectDropdown
                options={productUrls.map((item) => ({
                  value: item.key,
                  label: item.label,
                }))}
                selected={selectedProducts}
                onChange={setSelectedProducts}
                placeholder="All Products"
                testId="product-url-filter"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Currency</label>
              <MultiSelectDropdown
                options={[
                  { value: 'INR', label: 'INR (₹)' },
                  { value: 'USD', label: 'USD ($)' },
                ]}
                selected={selectedCurrencies}
                onChange={setSelectedCurrencies}
                placeholder="All Currencies"
                testId="currency-filter"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Views Section */}
      <div>
        <h3 className="text-xs uppercase tracking-wider font-semibold text-slate-400 mb-3">Page Views</h3>
        <div className="grid grid-cols-2 md:grid-cols-2 gap-4">
          <MetricCard
            label="Page Views"
            value={fmtNum(v.page_views)}
            sublabel="Total page view events"
            accent="blue"
            testId="metric-page-views"
          />
          <MetricCard
            label="Unique Sales Page Views"
            value={fmtNum(v.unique_page_views)}
            sublabel="Unique visitors"
            accent="indigo"
            testId="metric-unique-views"
          />
        </div>
      </div>

      {/* Total Paid Trials */}
      <div>
        <h3 className="text-xs uppercase tracking-wider font-semibold text-slate-400 mb-3">Total Paid Trials</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <MetricCard
            label="Total Paid Trials"
            value={fmtNum(s.total_paid_trials)}
            sublabel="Trial payments captured in period"
            accent="emerald"
            testId="metric-total-paid-trials"
            onViewLeads={() => openLeadsModal('Total Paid Trials', { metric: 'total_paid_trials' })}
          />
          <MetricCard
            label="Doctor Trials"
            value={fmtNum(s.doctor_trials)}
            sublabel="Doctor-linked leads"
            accent="purple"
            testId="metric-doctor-trials"
            onViewLeads={() => openLeadsModal('Doctor Trials', { metric: 'doctor_trials' })}
          />
          <MetricCard
            label="Perf. Marketing Trials"
            value={fmtNum(s.perf_marketing_trials)}
            sublabel="Performance Marketing funnel"
            accent="amber"
            testId="metric-pm-trials"
            onViewLeads={() => openLeadsModal('Perf. Marketing Trials', { metric: 'perf_marketing_trials' })}
          />
          <MetricCard
            label="Direct Trials"
            value={fmtNum(s.direct_trials)}
            sublabel="Website / Referral / Manual"
            accent="sky"
            testId="metric-direct-trials"
            onViewLeads={() => openLeadsModal('Direct Trials', { metric: 'direct_trials' })}
          />
        </div>
      </div>

      {/* Revenue Section */}
      <div>
        <h3 className="text-xs uppercase tracking-wider font-semibold text-slate-400 mb-3">Revenue</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <MetricCard
            label="Trial Revenue"
            value={fmtCurrency(r.trial_revenue)}
            sublabel="Trial payments captured in period"
            accent="emerald"
            testId="metric-trial-revenue"
          />
          <MetricCard
            label="Sales Revenue"
            value={fmtCurrency(r.sales_revenue)}
            sublabel="Enrolment payments in period"
            accent="green"
            testId="metric-sales-revenue"
          />
          <MetricCard
            label="Net Refunds"
            value={fmtCurrency(r.net_refunds)}
            sublabel="Total refunded"
            accent="red"
            testId="metric-net-refunds"
          />
        </div>
      </div>

      {/* Conversion Rates */}
      <div>
        <h3 className="text-xs uppercase tracking-wider font-semibold text-slate-400 mb-3">Conversion Rates</h3>
        <div className="grid grid-cols-2 gap-4">
          <MetricCard
            label="Sales Conversion"
            value={fmtPct(c.sales_conversion_rate)}
            sublabel="Perf. Marketing paid trials / Unique views"
            accent="emerald"
            testId="metric-sales-conv"
          />
          <MetricCard
            label="Checkout Conversion"
            value={fmtPct(c.checkout_conversion_rate)}
            sublabel="Total form fills / Unique views"
            accent="amber"
            testId="metric-checkout-conv"
          />
        </div>
      </div>

      {/* Sales Split Visualization */}
      {s.total_paid_trials > 0 && (
        <div className="card">
          <div className="card-header">
            <h3 className="font-semibold text-slate-900">Sales Split</h3>
            <p className="text-sm text-slate-500">Breakdown of {s.total_paid_trials} total paid trials by source type</p>
          </div>
          <div className="card-body">
            <div className="space-y-3">
              <SplitBar label="Doctor Trials" count={s.doctor_trials} total={s.total_paid_trials} color="bg-purple-500" />
              <SplitBar label="Perf. Marketing" count={s.perf_marketing_trials} total={s.total_paid_trials} color="bg-amber-500" />
              <SplitBar label="Direct Trials" count={s.direct_trials} total={s.total_paid_trials} color="bg-sky-500" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Reusable metric card for Sales Metrics tab
const MetricCard = ({ label, value, sublabel, accent = 'blue', testId, onViewLeads }) => {
  const borderColors = {
    blue: 'border-l-blue-500',
    indigo: 'border-l-indigo-500',
    emerald: 'border-l-emerald-500',
    green: 'border-l-green-500',
    purple: 'border-l-purple-500',
    amber: 'border-l-amber-500',
    red: 'border-l-red-500',
    sky: 'border-l-sky-500',
    slate: 'border-l-slate-400',
  };
  return (
    <div
      className={`bg-white rounded-lg border border-slate-200 border-l-4 ${borderColors[accent] || 'border-l-blue-500'} p-4`}
      data-testid={testId}
    >
      <div className="text-xs text-slate-500 mb-1">{label}</div>
      <div className="text-xl font-bold text-slate-900">{value}</div>
      {sublabel && <div className="text-[11px] text-slate-400 mt-1">{sublabel}</div>}
      {onViewLeads && (
        <button
          onClick={onViewLeads}
          className="inline-block mt-2 text-xs text-indigo-600 hover:text-indigo-800 font-medium transition-colors"
          data-testid={testId ? `${testId}-view-leads` : undefined}
        >
          View Leads &rarr;
        </button>
      )}
    </div>
  );
};

// Horizontal bar for sales split
const SplitBar = ({ label, count, total, color }) => {
  const pct = total > 0 ? (count / total * 100) : 0;
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm text-slate-700">{label}</span>
        <span className="text-sm font-medium text-slate-900">{count} ({pct.toFixed(1)}%)</span>
      </div>
      <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all duration-500`} style={{ width: `${Math.max(pct, 1)}%` }} />
      </div>
    </div>
  );
};
const FunnelTab = ({ filters }) => {
  const [funnelData, setFunnelData] = useState(null);
  const [funnelLoading, setFunnelLoading] = useState(true);

  useEffect(() => {
    const fetchFunnel = async () => {
      setFunnelLoading(true);
      try {
        const params = new URLSearchParams();
        if (filters.start_date) params.append('start_date', filters.start_date);
        if (filters.end_date) params.append('end_date', filters.end_date);
        const r = await axios.get(`${API_URL}/api/analytics/funnel?${params}`);
        setFunnelData(r.data);
      } catch (error) {
        toast.error('Failed to load funnel data');
      } finally {
        setFunnelLoading(false);
      }
    };
    fetchFunnel();
  }, [filters.start_date, filters.end_date]);

  if (funnelLoading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div></div>;
  if (!funnelData) return <div className="text-center py-12 text-slate-500">No funnel data available</div>;

  const stages = [
    { key: 'page_views', label: 'Page Views', color: 'bg-blue-500', count: funnelData.page_views || 0 },
    { key: 'leads', label: 'Leads', color: 'bg-indigo-500', count: funnelData.leads || 0 },
    { key: 'trial_paid', label: 'Trial Paid', color: 'bg-purple-500', count: funnelData.trial_paid || 0 },
    { key: 'enrolled', label: 'Enrolled', color: 'bg-emerald-500', count: funnelData.enrolled || 0 },
  ];
  const maxCount = Math.max(...stages.map(s => s.count), 1);

  return (
    <div className="space-y-6" data-testid="funnel-tab">
      <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
        <h3 className="text-lg font-semibold text-slate-900 mb-6">Conversion Funnel</h3>
        <div className="space-y-4">
          {stages.map((stage, i) => {
            const prevCount = i > 0 ? stages[i - 1].count : stage.count;
            const convRate = prevCount > 0 ? ((stage.count / prevCount) * 100).toFixed(1) : '0.0';
            return (
              <div key={stage.key}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-slate-700">{stage.label}</span>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-bold text-slate-900">{stage.count.toLocaleString()}</span>
                    {i > 0 && <span className="text-xs text-slate-500">{convRate}%</span>}
                  </div>
                </div>
                <div className="h-8 bg-slate-100 rounded-full overflow-hidden">
                  <div className={`h-full ${stage.color} rounded-full transition-all duration-500`} style={{ width: `${Math.max((stage.count / maxCount) * 100, 2)}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Source breakdown */}
      {funnelData.by_source && (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">By Source</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr className="text-left text-xs font-semibold text-slate-500 uppercase border-b">
                <th className="p-3">Source</th><th className="p-3 text-right">Leads</th><th className="p-3 text-right">Trials</th><th className="p-3 text-right">Enrolled</th><th className="p-3 text-right">Conv %</th>
              </tr></thead>
              <tbody>
                {funnelData.by_source.map(s => (
                  <tr key={s.source} className="border-b border-slate-100">
                    <td className="p-3 font-medium">{s.source}</td>
                    <td className="p-3 text-right">{s.leads}</td>
                    <td className="p-3 text-right">{s.trial_paid}</td>
                    <td className="p-3 text-right text-emerald-600 font-medium">{s.enrolled}</td>
                    <td className="p-3 text-right">{s.leads > 0 ? ((s.enrolled / s.leads) * 100).toFixed(1) : 0}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Campaign breakdown */}
      {funnelData.by_campaign && funnelData.by_campaign.length > 0 && (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900 mb-4">By Campaign</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr className="text-left text-xs font-semibold text-slate-500 uppercase border-b">
                <th className="p-3">Campaign</th><th className="p-3 text-right">Leads</th><th className="p-3 text-right">Trials</th><th className="p-3 text-right">Enrolled</th>
              </tr></thead>
              <tbody>
                {funnelData.by_campaign.slice(0, 20).map(c => (
                  <tr key={c.campaign} className="border-b border-slate-100">
                    <td className="p-3 font-medium truncate max-w-xs">{c.campaign}</td>
                    <td className="p-3 text-right">{c.leads}</td>
                    <td className="p-3 text-right">{c.trial_paid}</td>
                    <td className="p-3 text-right text-emerald-600 font-medium">{c.enrolled}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ============ AI STRATEGY TAB (integrated from MarketingIntelPage) ============
const AIStrategyTab = ({ filters, adAccounts }) => {
  const [metaData, setMetaData] = useState(null);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [loadingAI, setLoadingAI] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [aiQuestion, setAiQuestion] = useState('');
  const [aiAnswer, setAiAnswer] = useState(null);
  const [loadingAnswer, setLoadingAnswer] = useState(false);

  const fetchMetaData = async () => {
    setLoadingMeta(true);
    try {
      const params = new URLSearchParams();
      if (selectedAccount) params.append('ad_account_id', selectedAccount);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      const r = await axios.get(`${API_URL}/api/marketing-intel/overview?${params}`);
      setMetaData(r.data);
    } catch (error) {
      toast.error('Failed to fetch Meta data');
    } finally {
      setLoadingMeta(false);
    }
  };

  const runAIAnalysis = async () => {
    if (!metaData) {
      toast.error('Fetch Meta data first');
      return;
    }
    setLoadingAI(true);
    try {
      const r = await axios.post(`${API_URL}/api/marketing-intel/analyze`, {
        campaign_data: metaData,
        ad_account_id: selectedAccount || undefined,
      });
      setAiAnalysis(r.data);
    } catch (error) {
      toast.error('AI analysis failed');
    } finally {
      setLoadingAI(false);
    }
  };

  const askAIQuestion = async () => {
    if (!aiQuestion.trim()) return;
    if (!metaData) { toast.error('Fetch Meta data first'); return; }
    setLoadingAnswer(true);
    try {
      const r = await axios.post(`${API_URL}/api/marketing-intel/analyze`, {
        campaign_data: metaData,
        ad_account_id: selectedAccount || undefined,
        question: aiQuestion.trim(),
      }, { withCredentials: true });
      setAiAnswer(r.data);
    } catch (error) {
      toast.error('Failed to get AI response');
    } finally {
      setLoadingAnswer(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="ai-strategy-tab">
      <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">AI Marketing Strategy</h3>
            <p className="text-sm text-slate-500">Fetch live campaign data from Meta and get AI-powered recommendations</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <select value={selectedAccount} onChange={(e) => setSelectedAccount(e.target.value)} className="input-base text-sm w-64" data-testid="ai-ad-account-select">
            <option value="">All Ad Accounts</option>
            {adAccounts.map(a => <option key={a.ad_account_id} value={a.ad_account_id}>{a.ad_account_name}</option>)}
          </select>
          <button onClick={fetchMetaData} disabled={loadingMeta} className="btn-secondary text-sm" data-testid="fetch-meta-btn">
            {loadingMeta ? 'Fetching...' : 'Fetch from Meta'}
          </button>
          <button onClick={runAIAnalysis} disabled={loadingAI || !metaData} className="btn-primary text-sm" data-testid="run-ai-btn">
            {loadingAI ? 'Analyzing...' : 'Run AI Analysis'}
          </button>
        </div>
      </div>

      {metaData && (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
          <h4 className="font-semibold text-slate-900 mb-3">Live Campaign Data</h4>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs font-semibold text-slate-500 uppercase border-b">
                <th className="p-3">Campaign</th><th className="p-3 text-right">Spend</th><th className="p-3 text-right">Impressions</th><th className="p-3 text-right">Clicks</th><th className="p-3 text-right">CTR</th><th className="p-3 text-right">CPC</th>
              </tr></thead>
              <tbody>
                {(metaData.campaigns || []).map((c, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td className="p-3 font-medium truncate max-w-xs">{c.campaign_name || c.name}</td>
                    <td className="p-3 text-right">₹{parseFloat(c.spend || 0).toLocaleString()}</td>
                    <td className="p-3 text-right">{parseInt(c.impressions || 0).toLocaleString()}</td>
                    <td className="p-3 text-right">{parseInt(c.clicks || 0).toLocaleString()}</td>
                    <td className="p-3 text-right">{parseFloat(c.ctr || 0).toFixed(2)}%</td>
                    <td className="p-3 text-right">₹{parseFloat(c.cpc || 0).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {aiAnalysis && (
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-6 border border-indigo-200">
          <h4 className="text-lg font-semibold text-indigo-900 mb-4">AI Recommendations</h4>
          <div className="prose prose-sm prose-indigo max-w-none">
            <div className="whitespace-pre-wrap text-slate-700">{typeof aiAnalysis.analysis === 'string' ? aiAnalysis.analysis : JSON.stringify(aiAnalysis.analysis, null, 2)}</div>
          </div>
        </div>
      )}

      {/* AI Q&A Section */}
      {metaData && (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200" data-testid="ai-qa-section">
          <h4 className="font-semibold text-slate-900 mb-3">Ask AI About Your Campaigns</h4>
          <p className="text-sm text-slate-500 mb-4">Ask specific questions about your campaign performance, budget allocation, audience targeting, or optimization strategies.</p>
          <div className="flex gap-3">
            <input
              type="text"
              value={aiQuestion}
              onChange={(e) => setAiQuestion(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && askAIQuestion()}
              placeholder="e.g., Which campaign has the best ROI? How should I reallocate my budget?"
              className="input-base flex-1 text-sm"
              data-testid="ai-question-input"
            />
            <button onClick={askAIQuestion} disabled={loadingAnswer || !aiQuestion.trim()} className="btn-primary text-sm px-6 whitespace-nowrap" data-testid="ask-ai-btn">
              {loadingAnswer ? 'Thinking...' : 'Ask AI'}
            </button>
          </div>
          {aiAnswer && (
            <div className="mt-4 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-lg p-4 border border-indigo-200">
              <div className="prose prose-sm prose-indigo max-w-none">
                <div className="whitespace-pre-wrap text-slate-700">{typeof aiAnswer.analysis === 'string' ? aiAnswer.analysis : JSON.stringify(aiAnswer.analysis, null, 2)}</div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
